import PropTypes from 'prop-types';
import { Users, ArrowRight, AlertTriangle, ScrollText } from 'lucide-react';

const ToolResultCard = ({ toolName, result, onNavigate }) => {
  if (!result) return <FallbackCard reason='Empty tool result.' />;
  if (toolName === 'semantic_search') {
    return <UserSearchCard result={result} onNavigate={onNavigate} />;
  }
  if (toolName === 'list_recent_admin_actions') {
    return <RecentActionsCard result={result} />;
  }
  return <FallbackCard toolName={toolName} result={result} />;
};

const UserSearchCard = ({ result, onNavigate }) => {
  const rows = result?.results || [];
  return (
    <div className='rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1D2838] p-3'>
      <div className='mb-2 flex items-center gap-2 text-xs font-semibold text-gray-500 dark:text-gray-400'>
        <Users className='h-3.5 w-3.5 text-primary-green' />
        Found {rows.length} {rows.length === 1 ? 'user' : 'users'}
      </div>
      {rows.length === 0 ? (
        <p className='py-4 text-center text-xs text-gray-500 dark:text-gray-400'>
          No matches. Try a different name or email fragment.
        </p>
      ) : (
        <ul className='flex flex-col divide-y divide-gray-100 dark:divide-gray-800'>
          {rows.slice(0, 8).map((u) => (
            <li
              key={u.id}
              className='flex items-center justify-between gap-3 py-2'
            >
              <div className='min-w-0'>
                <p className='truncate text-sm font-semibold text-gray-900 dark:text-white'>
                  {`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}
                </p>
                <p className='truncate text-[11px] text-gray-500 dark:text-gray-400'>
                  {u.email} · {u.university_name || 'no client'} ·{' '}
                  {u.active_course_period_count} active{' '}
                  {u.active_course_period_count === 1 ? 'course' : 'courses'}
                </p>
              </div>
              <span className='shrink-0 rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300'>
                {u.type}
              </span>
            </li>
          ))}
        </ul>
      )}
      {rows.length > 0 ? (
        <button
          type='button'
          onClick={() => onNavigate('/super-admin/users')}
          className='mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#0F1A2A] px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 hover:border-primary-green hover:text-primary-green transition'
        >
          View in Users table
          <ArrowRight className='h-3 w-3' />
        </button>
      ) : null}
    </div>
  );
};

const RecentActionsCard = ({ result }) => {
  const actions = result?.actions || [];
  return (
    <div className='rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1D2838] p-3'>
      <div className='mb-2 flex items-center gap-2 text-xs font-semibold text-gray-500 dark:text-gray-400'>
        <ScrollText className='h-3.5 w-3.5 text-primary-green' />
        {actions.length} recent {actions.length === 1 ? 'action' : 'actions'}
      </div>
      {actions.length === 0 ? (
        <p className='py-4 text-center text-xs text-gray-500 dark:text-gray-400'>
          No mutations recorded yet.
        </p>
      ) : (
        <ul className='flex flex-col divide-y divide-gray-100 dark:divide-gray-800'>
          {actions.slice(0, 10).map((a) => (
            <li key={a.id} className='py-2 text-xs'>
              <p className='font-semibold text-gray-900 dark:text-white'>{a.action}</p>
              <p className='text-[11px] text-gray-500 dark:text-gray-400'>
                {a.target_entity_type || 'n/a'} · {a.status}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const FallbackCard = ({ toolName, result, reason }) => (
  <div className='rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-3 text-xs text-amber-800 dark:text-amber-200'>
    <div className='mb-1 flex items-center gap-1 font-semibold'>
      <AlertTriangle className='h-3 w-3' />
      {reason || `No renderer for tool "${toolName}"`}
    </div>
    <pre className='max-h-40 overflow-auto whitespace-pre-wrap text-[10px] opacity-80'>
      {JSON.stringify(result || {}, null, 2)}
    </pre>
  </div>
);

ToolResultCard.propTypes = {
  toolName: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  result: PropTypes.object,
  onNavigate: PropTypes.func
};

export default ToolResultCard;
